import { CSSResultGroup, html, LitElement, nothing, TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'
import { createEffect } from '@cloudsuite/mosaic-shared/scripts/signals.js'
import { style } from './product-specifications.style.js'
import {
  infoIconPositions,
  type InfoIconPosition,
  type ProductSpecificationItem,
  type ProductSpecificationGroupItem,
  type ProductSpecificationGroup,
} from './typing/product-specifications.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { PDP_PRODUCT_STORE, type PdpProductStore } from 'components/discovery/pdp/pdp-product/pdp-product-store.js'
import { renderTableLayout } from './layouts/specifications-table.js'
import { renderTextLayout } from './layouts/specifications-text.js'
import { renderImagesLayout } from './layouts/specifications-images.js'
import { HiddenFieldsMixin } from 'components/shared/hidden-fields/hidden-fields-mixin.js'
import { HeadingSlotMixin } from 'components/shared/heading/heading-slot-mixin.js'


export type {
  InfoIconPosition,
  ProductSpecificationItem,
  ProductSpecificationGroupItem,
  ProductSpecificationGroup,
}

export { infoIconPositions }

export const productSpecificationsHiddenFields = ['title', 'code', 'brand-name', 'ean'] as const

export const specificationLayouts = ['default', 'accordion'] as const
export type SpecificationLayout = typeof specificationLayouts[number]

export const accordionInitialStates = ['first-open', 'all-open', 'all-closed'] as const
export type AccordionInitialState = typeof accordionInitialStates[number]


const withoutHiddenProperties = (groups: ProductSpecificationGroup, hiddenPropertyIds: number[]): ProductSpecificationGroup => {
  if (!hiddenPropertyIds.length) return groups
  return groups
    .map(group => ({ ...group, properties: group.properties.filter(item => !hiddenPropertyIds.includes(item.property_id)) }))
    .filter(group => group.properties.length > 0)
}

const withoutEmptyValues = (groups: ProductSpecificationGroup<string | null>): ProductSpecificationGroup =>
  groups
    .map(group => ({ ...group, properties: group.properties.filter((item): item is ProductSpecificationItem => item.value !== null) }))
    .filter((group, index) => group.properties.length > 0 || groups[index]?.properties.length === 0)

@customElement('csc-product-specifications')
export class CSCProductSpecifications extends HiddenFieldsMixin(HeadingSlotMixin(LitElement), productSpecificationsHiddenFields) {
  static override styles: CSSResultGroup = [hostStyle, style]

  @inject(PDP_PRODUCT_STORE)
  private store!: PdpProductStore

  @property({ type: String })
  layout: SpecificationLayout = 'default'

  @property({ type: String, attribute: 'accordion-initial-state' })
  accordionInitialState: AccordionInitialState = 'first-open'

  @property({ type: String, attribute: 'info-icon-position' })
  infoIconPosition: InfoIconPosition = 'prefix'

  @property({ type: Array })
  specifications: ProductSpecificationGroup = []

  @property({
    type: Array,
    attribute: 'hidden-property-ids',
    converter: {
      fromAttribute: (value: string | null) =>
        value ? value.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !Number.isNaN(n)) : [],
      toAttribute: (value: number[]) => value.join(','),
    },
  })
  hiddenPropertyIds: number[] = []

  constructor() {
    super()
    createEffect(() => {
      const product = this.store.product()
      if (product?.attributes) this.specifications = withoutEmptyValues(product.attributes)
    })
  }

  private stripGroupNamePrefix(groupName: string): string {
    const separatorIndex = groupName.indexOf('-')
    return separatorIndex === -1 ? groupName : groupName.slice(separatorIndex + 1)
  }

  private isGeneralGroup(groupName: string): boolean {
    const name = this.stripGroupNamePrefix(groupName)
    return name === 'None' || name === ''
  }

  private renderGroupTitle(groupName: string): TemplateResult {
    const label = this.isGeneralGroup(groupName)
      ? t('general_group_name', { ns: 'csc-product-specifications', default: 'General' })
      : this.stripGroupNamePrefix(groupName)

    return html`<cse-text variant="heading-4" class="section-title">${label}</cse-text>`
  }

  private buildDefaultItems(): ProductSpecificationItem[] {
    const product = this.store.product() as { code?: string; brand?: { name: string }; code_ean?: string } | null
    const base = { cms_data: {}, description: '', group_display_mode: null, picture: '', css_color: '', property_picture: null, property_description: '', property_id: 0, uom: null }
    const items: ProductSpecificationItem[] = []
    if (product?.code && !this.isHidden('code')) items.push({ ...base, name: t('code_label', { ns: 'csc-product-specifications', default: 'Code' }) as unknown as string, value: product.code })
    if (product?.brand?.name && !this.isHidden('brand-name')) items.push({ ...base, name: t('brand_label', { ns: 'csc-product-specifications', default: 'Brand' }) as unknown as string, value: product.brand.name })
    if (product?.code_ean && !this.isHidden('ean')) items.push({ ...base, name: t('ean_label', { ns: 'csc-product-specifications', default: 'EAN' }) as unknown as string, value: product.code_ean })
    return items
  }

  private renderGroupContent(group: ProductSpecificationGroupItem): TemplateResult {
    const mode = group.properties[0]?.group_display_mode
    if (mode === 'text') return renderTextLayout(group.properties)
    if (mode === 'pictures') return renderImagesLayout(group.properties)
    const items = this.isGeneralGroup(group.group_name)
      ? [...this.buildDefaultItems(), ...group.properties]
      : group.properties
    return renderTableLayout(items, this.infoIconPosition)
  }

  private renderDefaultSection(group: ProductSpecificationGroupItem): TemplateResult {
    return html`
      <div class="section">
        ${!this.isHidden('title') ? this.renderGroupTitle(group.group_name) : nothing}
        ${this.renderGroupContent(group)}
      </div>
    `
  }

  private isAccordionOpen(index: number): boolean {
    if (this.accordionInitialState === 'all-open') return true
    if (this.accordionInitialState === 'all-closed') return false
    return index === 0
  }

  private renderAccordionSection(group: ProductSpecificationGroupItem, index: number): TemplateResult {
    return html`
      <cse-accordion ?open=${this.isAccordionOpen(index)} variant="panel">
        <div slot="title">${this.renderGroupTitle(group.group_name)}</div>
        <div slot="content">
          ${this.renderGroupContent(group)}
        </div>
      </cse-accordion>
    `
  }

  override render() {
    return html`
      <div class="csc-product-specifications">
        ${this.renderHeadingSlot()}
        <div class="specs-group">
          ${withoutHiddenProperties(this.specifications, this.hiddenPropertyIds).map((group, index) =>
            this.layout === 'accordion'
              ? this.renderAccordionSection(group, index)
              : this.renderDefaultSection(group)
          )}
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-product-specifications': CSCProductSpecifications
  }
}
